import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Wallet, ArrowUpRight, Users, Package, 
  Gift, Star, Send, FileText, LogOut, Bell, Menu, X,
  TrendingUp, Copy, ChevronRight, User, Shield, Headphones
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { userApi, settingsApi } from '../../lib/api';
import { toast } from 'sonner';

// Sub-pages
import UserOverview from './UserOverview';
import UserDeposit from './UserDeposit';
import UserWithdraw from './UserWithdraw';
import UserPackages from './UserPackages';
import UserReferral from './UserReferral';
import UserTransfer from './UserTransfer';
import UserStars from './UserStars';
import UserPromotions from './UserPromotions';
import UserTransactions from './UserTransactions';
import UserProfile from './UserProfile';
import UserSupport from './UserSupport';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_stake-blc/artifacts/qx16sddk_Screenshot__247_-removebg-preview%20(1).png";

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
  { path: '/dashboard/deposit', icon: Wallet, label: 'Deposit' },
  { path: '/dashboard/withdraw', icon: ArrowUpRight, label: 'Withdraw' },
  { path: '/dashboard/packages', icon: Package, label: 'Staking' },
  { path: '/dashboard/referral', icon: Users, label: 'Team' },
  { path: '/dashboard/transfer', icon: Send, label: 'Transfer' },
  { path: '/dashboard/stars', icon: Star, label: 'Star Program' },
  { path: '/dashboard/promotions', icon: Gift, label: 'Promotions' },
  { path: '/dashboard/transactions', icon: FileText, label: 'Transactions' },
  { path: '/dashboard/support', icon: Headphones, label: 'Support' },
  { path: '/dashboard/profile', icon: User, label: 'Profile & KYC' },
];

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsApi.get();
        setBlcPrice(response.data.settings?.blc_price || 0.027);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${user?.referral_code}`);
    toast.success('Referral link copied!');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-400 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-black/90 border-r border-white/5 backdrop-blur-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_MAIN} alt="STAKE BLC" className="h-8" />
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-4">
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Referral Card */}
          <div className="p-4 border-t border-white/5">
            <div className="glass rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-zinc-500 uppercase">Your Referral Code</p>
                <button 
                  onClick={copyReferralLink}
                  className="text-amber-400 hover:text-amber-300"
                  data-testid="copy-referral-btn"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              <p className="text-amber-400 font-mono font-bold text-sm">{user?.referral_code}</p>
            </div>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 sm:h-20 bg-black/80 border-b border-white/5 backdrop-blur-md">
          <div className="flex items-center justify-between h-full px-4 sm:px-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-zinc-400 hover:text-white p-1"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg text-white font-medium truncate">Welcome back!</h1>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* BLC Price Ticker */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 glass rounded-lg">
                <span className="text-amber-400 font-bold text-xs">BLC</span>
                <span className="text-white font-mono text-sm">${blcPrice.toFixed(3)}</span>
                <TrendingUp className="w-3 h-3 text-green-400" />
              </div>

              <button className="relative p-2 rounded-full glass text-zinc-400 hover:text-white">
                <Bell className="w-4 h-4" />
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              <Link 
                to="/dashboard/profile" 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                data-testid="profile-avatar"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold text-sm">
                  {user?.full_name?.charAt(0)?.toUpperCase()}
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6">
          <Routes>
            <Route index element={<UserOverview />} />
            <Route path="deposit" element={<UserDeposit />} />
            <Route path="withdraw" element={<UserWithdraw />} />
            <Route path="packages" element={<UserPackages />} />
            <Route path="referral" element={<UserReferral />} />
            <Route path="transfer" element={<UserTransfer />} />
            <Route path="stars" element={<UserStars />} />
            <Route path="promotions" element={<UserPromotions />} />
            <Route path="transactions" element={<UserTransactions />} />
            <Route path="support" element={<UserSupport />} />
            <Route path="profile" element={<UserProfile />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
